import React, { useState, useEffect } from 'react';
import Hero from '../common/Hero';
import Footer from '../common/Footer';
import { initAnimations } from '../../utils/animations';

const Donate = () => {
  const [donationAmount, setDonationAmount]     = useState('');
  const [customAmount, setCustomAmount]         = useState('');
  const [paymentMethod, setPaymentMethod]       = useState('card');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories]             = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [error, setError]         = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const apiBaseUrl = process.env.REACT_APP_BASE_URL;

  const validMethods = [
    "card", "klarna", "link", "cashapp", "amazon_pay",
    "paypal", "google_pay", "apple_pay", "afterpay_clearpay",
  ];

  useEffect(() => {
    initAnimations();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/category/get`, {
        headers: {
          'Authorization': 'Basic ' + btoa(process.env.REACT_APP_NAME + ':' + process.env.REACT_APP_PASS),
        },
      });
      const data = await res.json();
      if (data.status === 1) {
        setCategories(data.categories);
        if (data.categories.length > 0) setSelectedCategory(data.categories[0].name);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const breadcrumbs = [
    { text: 'Home', link: '/' },
    { text: 'Donate' },
  ];

  const donationOptions = [
    { id: 'amount-10',  value: '10',  label: '$10'  },
    { id: 'amount-20',  value: '20',  label: '$20'  },
    { id: 'amount-25',  value: '25',  label: '$25'  },
    { id: 'amount-50',  value: '50',  label: '$50'  },
    { id: 'amount-100', value: '100', label: '$100' },
  ];

  const paymentMethods = [
    { id: 'method-card',    value: 'card',       icon: 'far fa-credit-card',     name: 'Credit/Debit Card', description: 'Visa, Mastercard, American Express' },
    { id: 'method-klarna',  value: 'klarna',     icon: 'fas fa-money-bill-wave',  name: 'Klarna',            description: 'Pay in installments with Klarna'    },
    { id: 'method-cashapp', value: 'cashapp',    icon: 'fas fa-dollar-sign',     name: 'Cash App',          description: 'Pay with Cash App'                  },
    { id: 'method-amazon',  value: 'amazon_pay', icon: 'fab fa-amazon',          name: 'Amazon Pay',        description: 'Pay with your Amazon account'       },
  ];

  // Category icon map
  const categoryIcons = {
    'Zakat':     'fas fa-hand-holding-heart',
    'Sadaqah':   'fas fa-donate',
    'Masjid':    'fas fa-mosque',
    'Education': 'fas fa-book-open',
    'Iftar':     'fas fa-utensils',
    'General':   'fas fa-hands-helping',
  };
  const getCategoryIcon = (name) =>
    categoryIcons[name] || 'fas fa-hand-holding-usd';

  const handleDonationSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    if (!selectedCategory) {
      setError('Please select a donation category');
      setIsSubmitting(false);
      return;
    }
    if (!validMethods.includes(paymentMethod)) {
      setError('Selected payment method is not supported');
      setIsSubmitting(false);
      return;
    }
    const amount = parseFloat(donationAmount || customAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount greater than 0');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/paymentall/create-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(process.env.REACT_APP_NAME + ':' + process.env.REACT_APP_PASS),
        },
        body: JSON.stringify({ category: selectedCategory, amount, paymentMethod, isGuest: true }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Payment failed');
      if (!data.checkoutUrl)  throw new Error('Missing checkout URL');

      window.location.href = data.checkoutUrl;
    } catch (err) {
      console.error('Donation error:', err);
      setError(err.message);
      setIsSubmitting(false);
    }
  };

  // ─── Inline styles ────────────────────────────────────────────────────────
  const styles = {
    // Category pill
    catPill: (selected) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 18px',
      borderRadius: '50px',
      border: selected ? '2px solid var(--bs-primary)' : '2px solid #dee2e6',
      background: selected ? 'var(--bs-primary)' : '#fff',
      color: selected ? '#fff' : '#495057',
      fontWeight: selected ? '700' : '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontSize: '14px',
      boxShadow: selected ? '0 4px 12px rgba(13,110,253,0.3)' : '0 1px 3px rgba(0,0,0,0.06)',
      userSelect: 'none',
    }),
    catIcon: (selected) => ({
      fontSize: '15px',
      color: selected ? '#fff' : 'var(--bs-primary)',
    }),

    // Amount button
    amountBtn: (selected) => ({
      padding: '12px 0',
      minWidth: '75px',
      borderRadius: '12px',
      border: selected ? '2px solid var(--bs-primary)' : '2px solid #dee2e6',
      background: selected ? 'var(--bs-primary)' : '#fff',
      color: selected ? '#fff' : '#495057',
      fontWeight: selected ? '700' : '600',
      fontSize: '16px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: selected ? '0 4px 14px rgba(13,110,253,0.3)' : '0 1px 3px rgba(0,0,0,0.06)',
      flex: '1',
      textAlign: 'center',
      userSelect: 'none',
    }),

    // Payment method card
    methodCard: (selected) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      padding: '14px 18px',
      borderRadius: '14px',
      border: selected ? '2px solid var(--bs-primary)' : '2px solid #dee2e6',
      background: selected ? 'rgba(13,110,253,0.05)' : '#fff',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginBottom: '10px',
      boxShadow: selected ? '0 4px 14px rgba(13,110,253,0.15)' : '0 1px 3px rgba(0,0,0,0.06)',
      userSelect: 'none',
    }),
    methodIconWrap: (selected) => ({
      width: '44px',
      height: '44px',
      borderRadius: '12px',
      background: selected ? 'var(--bs-primary)' : 'rgba(13,110,253,0.08)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      transition: 'background 0.2s',
    }),
    methodIcon: (selected) => ({
      fontSize: '18px',
      color: selected ? '#fff' : 'var(--bs-primary)',
    }),
    checkBadge: {
      marginLeft: 'auto',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      background: 'var(--bs-primary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
  };

  return (
    <>
      <Hero title="Donate" breadcrumbs={breadcrumbs} />

      <div className="container-fluid py-5">
        <div className="container donation-container wow fadeIn" data-wow-delay="0.1s">
          <div className="text-center mb-5">
            <p className="fs-5 text-uppercase text-primary fw-semibold">Make a Donation</p>
            <h1 className="display-5 mb-0">Your Generosity Makes a Difference</h1>
          </div>

          <form id="donationForm" onSubmit={handleDonationSubmit}>
            <div className="donation-box">

              {/* ── Category ─────────────────────────────────────────────── */}
              <h5 className="mb-3 fw-bold d-flex align-items-center gap-2">
                <i className="fas fa-tags text-primary"></i>
                Select Donation Category
              </h5>
              {categoriesLoading ? (
                <div className="d-flex align-items-center gap-2 mb-4">
                  <div className="spinner-border spinner-border-sm text-primary" role="status" />
                  <span className="text-muted">Loading categories...</span>
                </div>
              ) : categories.length === 0 ? (
                <div className="alert alert-warning mb-4">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  No categories available. Please try again later.
                </div>
              ) : (
                <div className="d-flex flex-wrap gap-2 mb-4">
                  {categories.map(cat => (
                    <div
                      key={cat._id || cat.name}
                      style={styles.catPill(selectedCategory === cat.name)}
                      onClick={() => setSelectedCategory(cat.name)}
                      role="button"
                    >
                      <i className={getCategoryIcon(cat.name)} style={styles.catIcon(selectedCategory === cat.name)}></i>
                      {cat.name}
                      {selectedCategory === cat.name && (
                        <i className="fas fa-check" style={{ fontSize: '12px', marginLeft: '4px' }}></i>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* ── Amount ───────────────────────────────────────────────── */}
              <h5 className="mb-3 fw-bold d-flex align-items-center gap-2">
                <i className="fas fa-dollar-sign text-primary"></i>
                Select Donation Amount
              </h5>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {donationOptions.map(option => (
                  <div
                    key={option.id}
                    style={styles.amountBtn(donationAmount === option.value)}
                    onClick={() => { setDonationAmount(option.value); setCustomAmount(''); }}
                    role="button"
                  >
                    {option.label}
                    {donationAmount === option.value && (
                      <div style={{ fontSize: '10px', marginTop: '2px', opacity: 0.85 }}>✓ Selected</div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mb-4">
                <label htmlFor="other-amount" className="form-label text-muted fw-semibold" style={{ fontSize: '14px' }}>
                  Or Enter Custom Amount
                </label>
                <div className="input-group" style={{ maxWidth: '260px' }}>
                  <span className="input-group-text fw-bold">$</span>
                  <input
                    type="number"
                    className="form-control"
                    id="other-amount"
                    placeholder="0.00"
                    value={customAmount}
                    onChange={(e) => { setCustomAmount(e.target.value); setDonationAmount(''); }}
                    min="1"
                    step="0.01"
                  />
                </div>
              </div>

              {/* ── Payment method ───────────────────────────────────────── */}
              <h5 className="mb-3 fw-bold d-flex align-items-center gap-2">
                <i className="fas fa-credit-card text-primary"></i>
                Select Payment Method
              </h5>
              <div className="mb-2">
                {paymentMethods.map(method => {
                  const selected = paymentMethod === method.value;
                  return (
                    <div
                      key={method.id}
                      style={styles.methodCard(selected)}
                      onClick={() => setPaymentMethod(method.value)}
                      role="button"
                    >
                      <div style={styles.methodIconWrap(selected)}>
                        <i className={method.icon} style={styles.methodIcon(selected)}></i>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', fontSize: '15px', color: selected ? 'var(--bs-primary)' : '#212529' }}>
                          {method.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#868e96', marginTop: '2px' }}>
                          {method.description}
                        </div>
                      </div>
                      {selected && (
                        <div style={styles.checkBadge}>
                          <i className="fas fa-check" style={{ color: '#fff', fontSize: '11px' }}></i>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* ── Selected summary ─────────────────────────────────────── */}
              {(donationAmount || customAmount) && selectedCategory && (
                <div className="mt-3 p-3 rounded-3 d-flex align-items-center gap-3"
                  style={{ background: 'rgba(13,110,253,0.06)', border: '1px dashed rgba(13,110,253,0.3)' }}>
                  <i className="fas fa-info-circle text-primary fa-lg"></i>
                  <div style={{ fontSize: '14px' }}>
                    You are donating{' '}
                    <strong className="text-primary">
                      ${donationAmount || customAmount}
                    </strong>{' '}
                    to{' '}
                    <strong className="text-primary">{selectedCategory}</strong>{' '}
                    via <strong>{paymentMethods.find(m => m.value === paymentMethod)?.name}</strong>
                  </div>
                </div>
              )}

              {error && (
                <div className="alert alert-danger mt-3 d-flex align-items-center gap-2">
                  <i className="fas fa-exclamation-circle"></i>
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary btn-lg donate-btn mt-4 w-100"
                disabled={isSubmitting || categoriesLoading}
                style={{ borderRadius: '14px', padding: '14px', fontWeight: '700', fontSize: '16px' }}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-hand-holding-usd me-2"></i>
                    Donate Now
                  </>
                )}
              </button>

              <p className="text-center text-muted mt-3" style={{ fontSize: '12px' }}>
                <i className="fas fa-lock me-1"></i>
                Payments are securely processed by Stripe. We never store your card details.
              </p>
            </div>
          </form>

          {/* ── Benefits ── */}
          <div className="donation-benefits wow fadeIn" data-wow-delay="0.2s">
            <h4 className="mb-4">How Your Donation Helps</h4>
            <div className="benefit-item">
              <i className="fas fa-mosque"></i>
              <div><h6>Masjid Maintenance</h6><p className="small text-muted mb-0">Helps maintain our prayer facilities and keep them clean</p></div>
            </div>
            <div className="benefit-item">
              <i className="fas fa-book-open"></i>
              <div><h6>Islamic Education</h6><p className="small text-muted mb-0">Supports our Quran and Islamic studies programs</p></div>
            </div>
            <div className="benefit-item">
              <i className="fas fa-utensils"></i>
              <div><h6>Community Iftars</h6><p className="small text-muted mb-0">Provides food for Ramadan iftars and community meals</p></div>
            </div>
            <div className="benefit-item">
              <i className="fas fa-hands-helping"></i>
              <div><h6>Social Services</h6><p className="small text-muted mb-0">Funds our food pantry and assistance programs</p></div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Donate;